import { useEffect, useRef, useState } from "react";
import { Box, Chip, IconButton, ListItemButton, Menu, MenuItem, TextField, Typography } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useLastRunSummary } from "./hooks/useLastRunSummary";
import type { WorkflowDefinition } from "./workflow.types";

interface WorkflowListItemProps {
  workflow: WorkflowDefinition;
  selected: boolean;
  onSelect: () => void;
  onRename: (name: string) => void;
  onUpdateDescription: (description: string) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

export default function WorkflowListItem({
  workflow,
  selected,
  onSelect,
  onRename,
  onUpdateDescription,
  onDuplicate,
  onDelete,
}: WorkflowListItemProps) {
  const caption = useLastRunSummary(workflow._id);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [draftName, setDraftName] = useState(workflow.name);
  const [draftDescription, setDraftDescription] = useState(workflow.description ?? "");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const descriptionInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => setDraftName(workflow.name), [workflow.name]);
  useEffect(() => setDraftDescription(workflow.description ?? ""), [workflow.description]);
  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);
  useEffect(() => {
    if (isEditingDescription) descriptionInputRef.current?.focus();
  }, [isEditingDescription]);

  const commitRename = () => {
    setIsEditing(false);
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== workflow.name) onRename(trimmed);
    else setDraftName(workflow.name);
  };

  const commitDescription = () => {
    setIsEditingDescription(false);
    const trimmed = draftDescription.trim();
    if (trimmed !== (workflow.description ?? "").trim()) onUpdateDescription(trimmed);
  };

  return (
    <ListItemButton
      selected={selected}
      onClick={isEditing ? undefined : onSelect}
      sx={{ borderRadius: 1.5, mb: 0.5, alignItems: "flex-start", flexDirection: "column", py: 1 }}
    >
      <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 0.5 }}>
        {isEditing ? (
          <TextField
            inputRef={inputRef}
            variant="standard"
            size="small"
            value={draftName}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") {
                setDraftName(workflow.name);
                setIsEditing(false);
              }
            }}
            sx={{ flex: 1 }}
          />
        ) : (
          <Typography
            variant="body2"
            title={workflow.name}
            sx={{ fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
          >
            {workflow.name}
          </Typography>
        )}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setIsEditing(true);
            }}
          >
            Rename
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setIsEditingDescription(true);
            }}
          >
            {workflow.description ? "Edit description" : "Add description"}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onDuplicate();
            }}
          >
            Duplicate
          </MenuItem>
          <MenuItem
            sx={{ color: "error.main" }}
            onClick={() => {
              setAnchorEl(null);
              onDelete();
            }}
          >
            Delete
          </MenuItem>
        </Menu>
      </Box>
      {isEditingDescription ? (
        <TextField
          inputRef={descriptionInputRef}
          variant="standard"
          size="small"
          fullWidth
          multiline
          value={draftDescription}
          placeholder="Add a description…"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setDraftDescription(e.target.value)}
          onBlur={commitDescription}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setDraftDescription(workflow.description ?? "");
              setIsEditingDescription(false);
            }
          }}
          sx={{ mt: 0.25, "& textarea": { fontSize: 12 } }}
        />
      ) : (
        workflow.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            onDoubleClick={(e) => {
              e.stopPropagation();
              setIsEditingDescription(true);
            }}
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              mt: 0.25,
            }}
          >
            {workflow.description}
          </Typography>
        )
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
        <Chip
          size="small"
          label={workflow.enabled ? "Active" : "Inactive"}
          color={workflow.enabled ? "success" : "default"}
          variant={workflow.enabled ? "filled" : "outlined"}
          sx={{ height: 18, fontSize: 10 }}
        />
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </Box>
    </ListItemButton>
  );
}
